import { DivConst } from 'constants/divConst';
import { useState, type JSX } from 'react';
import type { VoiceChatReportQuestionType } from 'types/api/voiceChatReportRefResponseType';
import type { VoiceChatRespondentItemType } from 'types/api/voiceChatRespondentRefResponseType';
import type { VoiceChatViewerItemType } from 'types/api/voiceChatViewerRefResponseType';
import WsReportVoiceChatViewers from 'pages/ws/reportVoiceChat/components/reportVoiceChatViewers/WsReportVoiceChatViewers';

/**
 * ボイスチャットレポート 設問ごと／回答者ごと／視聴者一覧 タブコンポーネント（モック VcReport.tsx 250-406 行相当）。
 * KPI/期間/チャート/ヘッダは親（WsReportVoiceChat）で本物 presentational を流用するため含まない。
 */
export default function WsReportVoiceChatTabs(props: {
  questions: VoiceChatReportQuestionType[];
  respondents: VoiceChatRespondentItemType[];
  viewers: VoiceChatViewerItemType[];
  viewerFilterDiv: number;
  onChangeViewerFilter: (div: number) => void;
  onDownloadViewerCsv: (encoding?: 'utf8' | 'shift_jis' | null) => void;
}): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const [tab, setTab] = useState<'byQuestion' | 'byRespondent' | 'viewers'>('byQuestion');
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const selected = props.respondents.find(r => r.id === selectedId) ?? null;
  const answerQuestions = props.questions.filter(
    q => q.answerModeDiv === DivConst.items.voiceChatQuestionMode.explainAndAnswer.value
  );

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * 設問ごとに、書き起こし済みの回答を回答者情報付きで集める。
   */
  const _answersForQuestion = (
    no: number
  ): {
    answer: VoiceChatRespondentItemType['answers'][number];
    respondent: VoiceChatRespondentItemType;
  }[] =>
    props.respondents.flatMap(respondent =>
      respondent.answers
        .filter(answer => answer.no === no && answer.transcript)
        .map(answer => ({ answer, respondent }))
    );

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <section className="section-form-default" style={{ paddingTop: 8 }}>
      <div className="container">
        <div className="vcr-tabs">
          <button
            type="button"
            className={`vcr-tab${tab === 'byQuestion' ? ' active' : ''}`}
            onClick={() => setTab('byQuestion')}
          >
            設問ごと（AI要約）
          </button>
          <button
            type="button"
            className={`vcr-tab${tab === 'byRespondent' ? ' active' : ''}`}
            onClick={() => {
              setTab('byRespondent');
              setSelectedId(null);
            }}
          >
            回答者ごと
          </button>
          <button
            type="button"
            className={`vcr-tab${tab === 'viewers' ? ' active' : ''}`}
            onClick={() => setTab('viewers')}
          >
            視聴者一覧
          </button>
        </div>

        {/* ========== ① 設問ごと（横断 + AI要約） ========== */}
        {tab === 'byQuestion' && (
          <div className="box-common shadow-none">
            {answerQuestions.map(question => {
              const items = _answersForQuestion(question.no);
              return (
                <div className="vcr-qcard" key={question.no}>
                  <div className="vcr-qhead">
                    <span className="vcr-qno">{question.no}</span>
                    <span className="vcr-qtitle">{question.title}</span>
                    <span className="vcr-modetag">説明＋回答</span>
                    <span className="vcr-qcount">回答 {items.length} 件</span>
                  </div>

                  {question.summary && (
                    <div className="vcr-ai">
                      <div className="vcr-ai-head">✨ AIによる回答の要約</div>
                      <div className="vcr-ai-text">{question.summary}</div>
                      {question.keywords.length > 0 && (
                        <div className="vcr-kw">
                          {question.keywords.map(keyword => (
                            <span key={keyword}>{keyword}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {items.length === 0 ? (
                    <div className="vcr-muted">（まだ書き起こし済みの回答がありません）</div>
                  ) : (
                    items.map(({ answer, respondent }) => (
                      <div className="vcr-tr" key={respondent.id}>
                        <div className="vcr-transcript">{answer.transcript}</div>
                        {answer.supplement && (
                          <div className="vcr-supp">
                            <b>補足テキスト:</b> {answer.supplement}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ========== ② 回答者ごと ========== */}
        {tab === 'byRespondent' && selected === null && (
          <div className="box-common shadow-none">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <h3 className="title-section title-md mb-0">回答者一覧</h3>
              <span className="vcr-muted">全 {props.respondents.length} 件</span>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table className="vcr-table">
                <thead>
                  <tr>
                    <th>回答日時</th>
                    <th>回答者</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {props.respondents.map(respondent => (
                    <tr key={respondent.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{respondent.answeredAt}</td>
                      <td>
                        <div className="vcr-rname">{respondent.name}</div>
                        <div className="vcr-rmail">{respondent.mail}</div>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {respondent.transcriptStatusDiv ===
                        DivConst.items.transcriptStatus.done.value ? (
                          <span className="vcr-link" onClick={() => setSelectedId(respondent.id)}>
                            詳細を見る
                          </span>
                        ) : (
                          <span className="vcr-muted">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* 回答者詳細 */}
        {tab === 'byRespondent' && selected !== null && (
          <div className="box-common shadow-none">
            <span className="vcr-back" onClick={() => setSelectedId(null)}>
              ← 回答者一覧へ戻る
            </span>
            <div
              className="d-flex align-items-center justify-content-between mt-2 mb-3"
              style={{ flexWrap: 'wrap', gap: 8 }}
            >
              <h3 className="title-section title-md mb-0">
                {selected.name}（{selected.mail}）
              </h3>
              <span className="vcr-muted">回答日時: {selected.answeredAt}</span>
            </div>

            {props.questions.map(question => {
              const answer = selected.answers.find(a => a.no === question.no);
              const isExplainOnly =
                question.answerModeDiv === DivConst.items.voiceChatQuestionMode.explainOnly.value;
              return (
                <div className="vcr-qcard" key={question.no}>
                  <div className="vcr-qhead">
                    <span className="vcr-qno">{question.no}</span>
                    <span className="vcr-qtitle">{question.title}</span>
                    <span className="vcr-modetag">{isExplainOnly ? '説明のみ' : '説明＋回答'}</span>
                  </div>

                  {isExplainOnly ? (
                    <div className="vcr-muted">（説明のみの設問。回答録音はありません）</div>
                  ) : answer && answer.transcript ? (
                    <>
                      <div className="vcr-transcript">{answer.transcript}</div>
                      {answer.supplement && (
                        <div className="vcr-supp">
                          <b>補足テキスト:</b> {answer.supplement}
                        </div>
                      )}
                    </>
                  ) : answer &&
                    answer.answerModeDiv ===
                      DivConst.items.voiceChatQuestionMode.explainAndAnswer.value &&
                    answer.durationSec == null ? (
                    <div className="vcr-muted">（無回答）</div>
                  ) : (
                    <div className="vcr-muted">
                      （録音はありますが書き起こしに失敗、または未回答）
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ========== ③ 視聴者一覧 ========== */}
        {tab === 'viewers' && (
          <WsReportVoiceChatViewers
            viewers={props.viewers}
            filterDiv={props.viewerFilterDiv}
            onChangeFilter={props.onChangeViewerFilter}
            onDownloadCsv={props.onDownloadViewerCsv}
          />
        )}
      </div>
    </section>
  );
}
